import { useEffect } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faBoxOpen,
  faBoxesStacked,
  faBuilding,
  faFileInvoice,
  faFlaskVial,
  faHospital,
  faRulerCombined,
  faStore,
} from '@fortawesome/free-solid-svg-icons'

import { Footer, LogoLockup } from '../components/SiteChrome'
import { BrandTrustSections, type TrustQuestion } from '../components/BrandTrustSections'
import { AdNewsProofSection } from '../components/HomeNewsSection'
import { LpCrossLinks } from '../components/LpCrossLinks'
import { MedicalDocumentation, MedicalFaq, MEDICAL_FAQ } from '../components/MedicalDocumentation'
import { MedicalMaskCatalog, MedicalProductCatalog, MedicalWhatsAppLink } from '../components/MedicalProductCatalog'
import type { MedicalMaskCard, MedicalSupplyCard } from '../components/MedicalProductCatalog'
import { MedicalOrderChecklist, MedicalSelectionGuide } from '../components/MedicalSelectionGuide'
import { MEDICAL_MASK_DEFINITIONS, MEDICAL_SUPPLY_DEFINITIONS } from '../data/medicalSupplies'
import { trackViewItemList } from '../lib/analytics'
import {
  getProductPrimaryImage,
  getStorefrontConfig,
  listStorefrontCategories,
  listStorefrontProducts,
  toExternalImage,
} from '../lib/dropify'
import type { StorefrontCategory, StorefrontConfig, StorefrontProduct } from '../lib/dropify'
import { INDEXABLE_ROBOTS, SITE_NAME, SITE_URL, absoluteUrl, brandTitle, buildBreadcrumbSchema, buildFaqSchema } from '../lib/seo'
import { getBrandProofImages } from '../lib/testimonials'
import { listDashboardNewsPosts } from '../lib/news'

const PAGE_PATH = '/perlengkapan-medis'
const PAGE_NAME = 'Grosir Perlengkapan Medis: Sarung Tangan & Masker'
const PAGE_DESCRIPTION =
  'Sarung tangan medis latex, nitrile, dan masker COMET untuk rumah sakit, klinik, dan toko alkes. Grosir dari distributor tangan pertama Surabaya. Cek stok via WhatsApp.'
const IMAGE_TRANSFORM = { width: 680, height: 820, fitIn: true, format: 'webp', quality: 76 } as const
const MASK_IMAGE_TRANSFORM = { width: 680, height: 680, fitIn: true, format: 'webp', quality: 76 } as const

export const MEDICAL_TRUST_QUESTIONS: TrustQuestion[] = [
  {
    q: 'Apakah pesanan sarung tangan 10 box boleh campur produk atau ukuran?',
    a: 'Boleh diajukan. Kirim pembagiannya, misalnya 5 box Shamrock Nitrile ukuran M dan 5 box NAYA Latex ukuran L. Admin memeriksa stok setiap kombinasi sebelum quotation dibuat.',
  },
  {
    q: 'Bisakah meminta dokumen izin edar sebelum mengirim PO?',
    a: 'Bisa. Sebutkan dokumen yang diwajibkan fasilitas atau tender Anda, misalnya izin edar AKD, data TKDN, atau sertifikat halal. Admin memeriksa ketersediaan dan masa berlakunya untuk SKU yang dipilih.',
  },
  {
    q: 'Apakah pengiriman menjangkau kota di luar Surabaya?',
    a: 'Pesanan diproses dari Surabaya dan dapat dikirim ke seluruh Indonesia. Kirim kota dan kecamatan tujuan agar admin menghitung ongkir serta memilih ekspedisinya.',
  },
  {
    q: 'Bisakah Dropify menjadi pemasok rutin fasilitas Anda?',
    a: 'Kirim perkiraan pemakaian bulanan beserta pembagian produk dan ukuran. Admin memeriksa kesiapan stok dan membahas pola pengiriman berkala bersama Anda.',
  },
  {
    q: 'Bagaimana jika ukuran yang dibutuhkan sedang kosong?',
    a: 'Admin menyampaikan stok per ukuran apa adanya. Anda dapat menyesuaikan pembagian ukuran atau memilih produk lain dengan bahan dan spesifikasi serupa dari daftar.',
  },
]

const BUYER_SEGMENTS = [
  {
    icon: faHospital,
    title: 'Rumah sakit & klinik',
    desc: 'Kebutuhan rutin lintas unit, dari sarung tangan pemeriksaan sampai masker. Sertakan daftar SKU dan dokumen yang diwajibkan bagian pengadaan sejak chat pertama.',
  },
  {
    icon: faFlaskVial,
    title: 'Laboratorium',
    desc: 'Sembilan dari sebelas sarung tangan dalam daftar ini powder free. Konfirmasikan kecocokannya dengan prosedur lab Anda sebelum PO.',
  },
  {
    icon: faStore,
    title: 'Apotek & toko alat kesehatan',
    desc: 'Stok jualan sarung tangan dan masker untuk dijual kembali. Permintaan campur produk atau ukuran diperiksa dari stok yang tersedia.',
  },
  {
    icon: faBuilding,
    title: 'Tim pengadaan perusahaan',
    desc: 'Quotation, invoice, serta pengecekan izin edar AKD dan TKDN per SKU membantu kelengkapan dokumen PO Anda.',
  },
]

const ORDER_STEPS = [
  { number: '01', title: 'Kirim kebutuhan', desc: 'Sebutkan produk, ukuran atau warna, jumlah box, kota tujuan, dan dokumen yang diwajibkan.' },
  { number: '02', title: 'Terima quotation', desc: 'Admin memeriksa stok per ukuran atau tipe, lalu mengirim harga, ongkir, dan status dokumen SKU pilihan Anda.' },
  { number: '03', title: 'Konfirmasi dan terima barang', desc: 'Setelah mengonfirmasi pembayaran, admin akan menyiapkan pesanan untuk dikirim dari Surabaya ke alamat Anda.' },
]

function resolveProducts(liveProducts: StorefrontProduct[]) {
  const liveBySlug = new Map(liveProducts.map((product) => [product.slug, product]))
  return MEDICAL_SUPPLY_DEFINITIONS.map((definition) => {
    const live = liveBySlug.get(definition.slug)
    return {
      ...definition,
      image: live
        ? getProductPrimaryImage(live, IMAGE_TRANSFORM)
        : toExternalImage(definition.image, IMAGE_TRANSFORM),
    }
  })
}

function resolveMasks(liveProducts: StorefrontProduct[]) {
  const liveBySlug = new Map(liveProducts.map((product) => [product.slug, product]))
  return MEDICAL_MASK_DEFINITIONS.map((definition) => {
    const live = liveBySlug.get(definition.slug)
    return {
      ...definition,
      image: live
        ? getProductPrimaryImage(live, MASK_IMAGE_TRANSFORM)
        : toExternalImage(definition.image, MASK_IMAGE_TRANSFORM),
    }
  })
}

export const Route = createFileRoute('/perlengkapan-medis')({
  loader: async () => {
    const [config, categories, response, proofImages, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      listStorefrontProducts({ category: 'perlengkapan-medis', per_page: 100 }).catch(() => null),
      getBrandProofImages().catch(() => [] as string[]),
      listDashboardNewsPosts().catch(() => []),
    ])

    return {
      config,
      categories,
      products: resolveProducts(response?.items ?? []),
      masks: resolveMasks(response?.items ?? []),
      proofImages,
      newsPosts,
    }
  },
  head: ({ loaderData }) => {
    const title = brandTitle(PAGE_NAME)
    const products = loaderData?.products ?? resolveProducts([])
    const masks = loaderData?.masks ?? resolveMasks([])
    const heroImage = products[0]?.image

    return {
      meta: [
        { title },
        { name: 'description', content: PAGE_DESCRIPTION },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: PAGE_DESCRIPTION },
        { property: 'og:url', content: absoluteUrl(PAGE_PATH) },
        { property: 'og:locale', content: 'id_ID' },
        ...(heroImage ? [{ property: 'og:image', content: heroImage }] : []),
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: PAGE_DESCRIPTION },
        ...(heroImage ? [{ name: 'twitter:image', content: heroImage }] : []),
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PAGE_PATH) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            '@id': `${absoluteUrl(PAGE_PATH)}#webpage`,
            url: absoluteUrl(PAGE_PATH),
            name: title,
            description: PAGE_DESCRIPTION,
            inLanguage: 'id-ID',
            isPartOf: { '@type': 'WebSite', '@id': `${SITE_URL}/#website`, name: SITE_NAME, url: SITE_URL },
          }),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBreadcrumbSchema([
            { name: 'Beranda', path: '/' },
            { name: 'Perlengkapan Medis', path: PAGE_PATH },
          ])),
        },
        { type: 'application/ld+json', children: JSON.stringify(buildFaqSchema([...MEDICAL_FAQ, ...MEDICAL_TRUST_QUESTIONS])) },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Perlengkapan medis di Dropify',
            numberOfItems: products.length + masks.length,
            itemListElement: [...products, ...masks].map((product, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@type': 'Thing',
                name: product.name,
                sku: product.sku,
                image: product.image,
                url: absoluteUrl(`/produk/${product.slug}`),
              },
            })),
          }),
        },
      ],
    }
  },
  component: MedicalSuppliesRoute,
})

function MedicalSuppliesRoute() {
  const { config, categories, products, masks, proofImages, newsPosts } = Route.useLoaderData()
  return <MedicalSuppliesLandingPage config={config} categories={categories} products={products} masks={masks} proofImages={proofImages} newsPosts={newsPosts} />
}

export function MedicalSuppliesLandingPage({
  config = null,
  categories = [],
  products = resolveProducts([]),
  masks = resolveMasks([]),
  proofImages = [],
  newsPosts = [],
}: {
  config?: StorefrontConfig | null
  categories?: StorefrontCategory[]
  products?: MedicalSupplyCard[]
  masks?: MedicalMaskCard[]
  proofImages?: string[]
  newsPosts?: import('../lib/dropify').StorefrontBlogPost[]
}) {
  useEffect(() => {
    trackViewItemList(
      'perlengkapan_medis_catalog',
      [...products, ...masks].map((product, index) => ({
        item_id: product.sku,
        item_name: product.name,
        item_category: 'Perlengkapan Medis',
        index,
      })),
    )
  }, [products, masks])

  const heroProducts = [products[0], masks[0], products[1]].filter(Boolean)
  const totalProducts = products.length + masks.length

  return (
    <div className="min-h-screen bg-white text-slate-950">
      <div className="bg-sky-100 px-4 py-2 text-center text-xs font-black text-sky-950 sm:text-sm">
        {products.length} sarung tangan medis & {masks.length} masker COMET · MOQ sarung tangan 10 box · kirim se-Indonesia
      </div>

      <header className="border-b border-white/10 bg-[#102d3d] text-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <a href="/" aria-label="Kembali ke Dropify"><LogoLockup size="sm" invert /></a>
          <MedicalWhatsAppLink
            label="Minta quotation"
            placement="header"
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-sky-200/40 px-4 py-2.5 text-sm font-bold text-sky-50 transition-colors hover:bg-white hover:text-slate-950"
          />
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-[#102d3d] text-white">
          <div aria-hidden className="absolute -left-32 bottom-0 h-72 w-72 rounded-full bg-emerald-400/10 blur-3xl" />
          <div aria-hidden className="absolute -right-20 top-12 h-96 w-96 rounded-full border-[64px] border-sky-300/5" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 md:py-20 lg:grid-cols-[1fr_1fr]">
            <div>
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-black uppercase tracking-wider text-sky-950">Perlengkapan medis grosir</span>
                <span className="rounded-full border border-white/20 px-3 py-1 text-xs font-bold text-slate-300">Distributor tangan pertama di Surabaya</span>
              </div>
              <h1 className="mt-6 text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
                Grosir sarung tangan medis dan masker <span className="text-sky-300">untuk rumah sakit, klinik, laboratorium, dan toko alkes.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-200 md:text-lg">
                {products.length} pilihan sarung tangan pemeriksaan latex dan nitrile ditambah {masks.length} tipe masker COMET Theo. Admin memeriksa stok per ukuran atau tipe, menyiapkan quotation, dan mengecek dokumen izin per SKU.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <MedicalWhatsAppLink
                  label="Cek stok dan minta quotation"
                  placement="hero_primary"
                  className="inline-flex min-h-12 items-center justify-center gap-2.5 rounded-xl bg-green-700 px-6 py-4 text-base font-black text-white shadow-lg shadow-black/20 transition-colors hover:bg-green-800"
                />
                <a href="#produk-medis" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-white/20 px-6 py-4 text-sm font-black transition-colors hover:bg-white/10">
                  Lihat {totalProducts} produk <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
                </a>
              </div>
              <p className="mt-3 text-sm text-slate-400">Belum pasti bahannya? Sebutkan penggunaan dan SOP fasilitas Anda, admin bantu memilih.</p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {heroProducts.map((product, index) => (
                <a key={product.sku} href={`/produk/${product.slug}`} className={`overflow-hidden rounded-3xl bg-white p-2 shadow-2xl ${index === 1 ? 'translate-y-7' : ''}`}>
                  <img src={product.image} alt={`${product.name} kemasan box`} width="520" height="620" fetchPriority={index === 0 ? 'high' : undefined} decoding="async" className="aspect-[4/5] w-full object-contain" />
                  <p className="line-clamp-2 px-2 pb-2 pt-3 text-xs font-black leading-snug text-slate-950 sm:text-sm">{product.name}</p>
                </a>
              ))}
            </div>
          </div>

          <div className="relative border-t border-white/10">
            <div className="mx-auto grid max-w-7xl divide-y divide-white/10 px-4 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              {[
                { icon: faBoxesStacked, value: 'MOQ 10 box', label: 'sarung tangan medis, masker konfirmasi admin' },
                { icon: faRulerCombined, value: 'XS sampai XL', label: 'admin mengonfirmasi ukuran per produk' },
                { icon: faBoxOpen, value: '20 sampai 100 pcs', label: 'isi per box mengikuti produk' },
              ].map((item) => (
                <div key={item.value} className="flex items-center gap-4 py-5 sm:px-6">
                  <FontAwesomeIcon icon={item.icon} className="h-6 w-6 text-sky-300" />
                  <div><p className="font-black">{item.value}</p><p className="text-xs text-slate-400">{item.label}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-emerald-100 bg-emerald-50 py-10 md:py-12">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-700">Cara order grosir</p>
              <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-950 md:text-3xl">
                Pesan lewat WhatsApp, satu admin menangani sampai barang dikirim
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 md:text-base">
                Seluruh pemesanan dilayani lewat WhatsApp, tanpa checkout di website. Pesanan diproses dari Surabaya dan dikirim ke seluruh Indonesia.
              </p>
            </div>
            <ol className="grid gap-3 sm:grid-cols-3">
              {ORDER_STEPS.map((step) => (
                <li key={step.number} className="rounded-2xl border border-emerald-100 bg-white p-4 shadow-sm">
                  <span className="text-xs font-black text-emerald-700">{step.number}</span>
                  <h3 className="mt-2 text-sm font-black text-slate-950">{step.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-slate-600">{step.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <AdNewsProofSection posts={newsPosts} />

        <section className="border-b border-slate-200 bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-sky-800">Siapa yang memesan</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Data yang perlu Anda siapkan sesuai jenis fasilitas</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Setiap kartu merangkum data yang mempercepat quotation untuk jenis pembeli itu. Salon atau studio yang memakai warna hitam dan pink dilayani dengan minimum pemesanan yang sama.
              </p>
            </div>
            <div className="mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {BUYER_SEGMENTS.map((segment) => (
                <article key={segment.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#102d3d] text-sky-300">
                    <FontAwesomeIcon icon={segment.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-black tracking-tight">{segment.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{segment.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <MedicalSelectionGuide />
        <MedicalProductCatalog products={products} />
        <MedicalMaskCatalog masks={masks} />

        <MedicalDocumentation />

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <MedicalOrderChecklist />
            <div className="lg:pl-8">
              <p className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-sky-800"><FontAwesomeIcon icon={faFileInvoice} className="h-4 w-4" />Proses quotation</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Enam data ini menentukan isi quotation Anda</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">Admin akan mencocokkan stok, ukuran, jumlah, harga, ongkir, dan dokumen yang tersedia. Jika barang ditujukan untuk tender atau SOP fasilitas tertentu, sertakan persyaratannya sejak awal.</p>
              <MedicalWhatsAppLink label="Kirim kebutuhan ke admin" placement="order_process" className="mt-6 inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-green-700 px-6 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800" />
            </div>
          </div>
        </section>

        <MedicalFaq />

        <section className="bg-sky-200 py-14 text-slate-950">
          <div className="mx-auto flex max-w-7xl flex-col justify-between gap-7 px-4 lg:flex-row lg:items-center">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-sky-900">Cek stok hari ini</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Kirim daftar kebutuhan Anda. Admin membalas dengan stok, harga, dan status dokumen.</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-700 md:text-base">Sertakan ukuran, jumlah box, kota tujuan, dan dokumen yang diwajibkan agar quotation pertama langsung lengkap.</p>
            </div>
            <MedicalWhatsAppLink
              label="Chat admin perlengkapan medis"
              placement="final_cta"
              className="inline-flex min-h-12 flex-shrink-0 items-center justify-center gap-2.5 rounded-xl bg-green-700 px-7 py-4 text-base font-black text-white transition-colors hover:bg-green-800"
            />
          </div>
        </section>

        <BrandTrustSections brandName="Perlengkapan Medis" itemName="perlengkapan medis" images={proofImages} questions={MEDICAL_TRUST_QUESTIONS} />

        <section className="border-t border-slate-200 bg-slate-50 py-12">
          <div className="mx-auto max-w-7xl px-4">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-sky-800">Kategori terkait</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a href="/sarung-tangan-pabrik" className="inline-flex min-h-11 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition-colors hover:border-sky-300 hover:text-sky-800"><FontAwesomeIcon icon={faBuilding} className="h-4 w-4" />Sarung tangan kerja pabrik</a>
              <a href="/supplier-apd-pabrik" className="inline-flex min-h-11 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition-colors hover:border-sky-300 hover:text-sky-800"><FontAwesomeIcon icon={faBoxesStacked} className="h-4 w-4" />APD untuk pengadaan pabrik</a>
            </div>
          </div>
        </section>

        <LpCrossLinks currentPath={PAGE_PATH} />
      </main>

      <Footer config={config} categories={categories} />
    </div>
  )
}
